import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  Renderer2,
  ElementRef,
  OnDestroy,
  AfterViewInit
} from '@angular/core';
import { FormControl, FormGroup, FormArray, Validators } from '@angular/forms';
import { takeWhile } from 'rxjs/operators';
import { Utilities, DateUtilities } from '@approot/utilities';
import { ParameterOption } from '@approot/models';

@Component({
  selector: 'app-schedule-reports-parameters',
  templateUrl: './schedule-reports-parameters.component.html',
  styleUrls: ['./schedule-reports-parameters.component.scss']
})
export class ScheduleReportsParametersComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() public modalTitle: string;
  @Input() public parametersFormGroup: FormGroup;
  @Input() public entities: string[];
  @Input() public allCurrencyPairs: string[] = [];
  @Input() public outputFields: ParameterOption[] = [];
  @Input() public parameters: ParameterOption[] = [];
  @Input() public showModal = false;
  @Output() public modalOpenStatusChanged = new EventEmitter();
  public parametersFormGroupForSubModal: FormGroup;
  public parametersToViewOnSubModal: ParameterOption[] = [];
  public showParameterSubModal: boolean;
  public subModalTitle = '';
  public typeOptions: string[] = [];
  public underlyingOptions: string[] = [];
  public underlyingOptionsLoading: boolean;
  public selectedReportParametersIndex: number = null;
  public loadingReports = false;

  constructor(private renderer: Renderer2, private elementRef: ElementRef) {}
  ngOnDestroy(): void {
    this.showModal = false;
  }
  ngAfterViewInit() {
    this.parameters.forEach(parameter => {
      // we need to add a percentage symbol to the end of text boxes that should be in a percentage format
      switch (parameter.type) {
        case 'percent':
          this.parametersFormGroup
            .get(parameter.shortName)
            .valueChanges.pipe(takeWhile(() => this.showModal))
            .subscribe(val => {
              Utilities.endOfTextBoxAdder(
                this.elementRef.nativeElement.querySelector(
                  '#' + parameter.shortName
                ),
                this.parametersFormGroup.get(parameter.shortName),
                '%',
                !!val,
                'percentage',
                this.renderer
              );
            });
          break;
        case 'date':
          if (this.parametersFormGroup.get(parameter.shortName).valid) {
            const dateInput = document.getElementById(parameter.shortName);

            this.renderer.setProperty(
              dateInput,
              'value',
              this.parametersFormGroup.get(parameter.shortName).value
            );
          }
          this.parametersFormGroup
            .get(parameter.shortName)
            .valueChanges.subscribe(val => {
              DateUtilities.onDateChanged(
                this.parametersFormGroup.get(parameter.shortName)
              ); // when date changes format it
            });
          break;
      }

      switch (parameter.shortName) {
        case 'underlyingType':
          // the value in the underlying type dictates the options for the underlying field
          const underlyingParameter = this.parameters.find(
            findParameter => findParameter.shortName === 'underlying'
          );

          this.parametersFormGroup
            .get(parameter.shortName)
            .valueChanges.subscribe(val => {
              this.parametersFormGroup.get('underlying').setValue('');

              this.setUnderlyingOptions(val, underlyingParameter);
            });

          break;
      }
    });
  }

  setUnderlyingOptions(typeValue: string, parameter: ParameterOption) {
    if (typeValue === 'FX Vol' || typeValue === 'FX Spot') {
      parameter.options = this.allCurrencyPairs;
    } else if (typeValue === 'IR Index') {
      parameter.options = this.setIfUnderlyingTypeIsIrIndex;
    }
  }

  get setIfUnderlyingTypeIsIrIndex() {
    return ['3m USD LIBOR'];
  }

  formFieldValidity(field: string) {
    return this.parametersFormGroup.get(field).valid;
  }
  ngOnInit() {
    if (this.outputFields && this.outputFields.length) this.setTypeOptions();
  }

  setTypeOptions() {
    this.typeOptions = this.outputFields.map(field => field.longName);
  }

  setUnderlyingOutputOptions(formControl: FormControl) {
    const type = formControl.get('type').value;
    const outPutField = this.outputFields.find(
      element => element.longName === type
    );
    if (outPutField) {
      if (outPutField.dynamicOptionsToUse === 'currencyPairs') {
        this.underlyingOptions = this.allCurrencyPairs;
      } else {
        this.underlyingOptions = outPutField.options;
      }
    } else {
      this.underlyingOptions = [];
    }
  }

  deleteReportParametersOutput() {
    (this.parametersFormGroup.get('output') as FormArray).removeAt(
      this.selectedReportParametersIndex
    );
    this.parametersFormGroup.get('output').markAsDirty(); // mark array as dirty
    this.selectedReportParametersIndex = null; // no row is currently selected
  }

  underlyingOptionDisabled(ctrl: FormControl) {
    //  underlying output field is disabled if the type is Price
    return ctrl.get('type').value === 'Price';
  }

  textBoxCaretRestrictionHandler(event, action: string) {
    // used to get the index of the item separating editable parts of text and non editable parts
    const indexLocator = Utilities.getIndexOfSeparator(event.target.value, '%');
    if (action === 'keydown') {
      Utilities.textBoxKeydownRestrictor(event, indexLocator, this.renderer);
    } else {
      Utilities.textBoxClickRestrictor(event, indexLocator, this.renderer);
    }
  }

  openParametersModal(itemName: string) {
    const itemFields = this.parameters.find(
      parameter => parameter.shortName === itemName
    );

    this.parametersToViewOnSubModal = itemFields.parameters;

    this.subModalTitle = 'Parameters For ' + itemFields.longName;
    this.parametersFormGroupForSubModal = this.parametersFormGroup.get(
      itemName
    ) as FormGroup;
    this.showParameterSubModal = true; // open the modal
  }

  modalChanged(event) {
    this.modalOpenStatusChanged.emit(event);
  }

  parametersModalStatusChanged(event: boolean) {
    this.showParameterSubModal = event; // hide/show parameters modal
  }

  get outputFormArray() {
    return (this.parametersFormGroup.get('output') as FormArray)
      .controls as FormControl[];
  }

  addNewOutput() {
    // adds a new output to the array
    const newoutput = new FormGroup({
      type: new FormControl('', [Validators.required]),
      underlying: new FormControl('')
    });
    newoutput
      .get('underlying')
      .setValidators([
        Utilities.outputUnderlyingValidator.bind(this, this.outputFields)
      ]);
    (this.parametersFormGroup.get('output') as FormArray).push(newoutput);
  }

  typeChange(ctrl: FormControl) {
    // when the type changes wipe the underlying value
    ctrl.get('underlying').setValue('');
  }
}
